import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const WeightUnit = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('weight_units');
  const [viewItem, setViewItem] = useState(null);
  const [weightUnits, setWeightUnits] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);

  const loadWeightUnits = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/weight-units`);
      setWeightUnits(response.data || []);
    } catch (error) {
      console.error('Error loading weight units:', error);
      toast.error('Failed to load weight units');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWeightUnits();
  }, [loadWeightUnits]);

  const handleOpenDialog = (unit = null) => {
    setEditingUnit(unit);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      weight_unit_code: (formData.get('weight_unit_code') || '').toUpperCase(),
      commercial: (formData.get('commercial') || '').toUpperCase(),
      measurement_unit_text: formData.get('measurement_unit_text')
    };

    try {
      if (editingUnit) {
        await axios.put(`${BACKEND_URL}/api/weight-units/${editingUnit.weight_unit_code}`, {
          commercial: data.commercial,
          measurement_unit_text: data.measurement_unit_text
        });
        toast.success('Weight Unit updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/weight-units`, data);
        toast.success('Weight Unit created successfully');
      }
      setIsDialogOpen(false);
      loadWeightUnits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save weight unit');
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Weight Units</h1>
            <p className="text-sm text-gray-500 mt-1">Manage weight measurement units for materials</p>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Weight Units</h2>
            {writable && (<Button onClick={() => handleOpenDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Weight Unit
            </Button>)}
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
              <p className="mt-2 text-gray-500">Loading...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Weight Unit Code</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Commercial</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Measurement Unit Text</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {weightUnits.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-8 text-gray-500">
                        No weight units found. Click &quot;Add Weight Unit&quot; to create one.
                      </td>
                    </tr>
                  ) : (
                    weightUnits.map((unit) => (
                      <tr key={unit.weight_unit_code} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 font-mono text-sm font-semibold">{unit.weight_unit_code}</td>
                        <td className="py-3 px-4 text-sm font-mono">{unit.commercial}</td>
                        <td className="py-3 px-4 text-sm">{unit.measurement_unit_text}</td>
                        <td className="py-3 px-4 text-right">
                          <RecordActions doctype="weight_units" onView={() => setViewItem(unit)} onEdit={() => handleOpenDialog(unit)} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingUnit ? 'Edit Weight Unit' : 'Add Weight Unit'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="weight_unit_code">Weight Unit Code * (3 characters)</Label>
                  <Input
                    id="weight_unit_code"
                    name="weight_unit_code"
                    defaultValue={editingUnit?.weight_unit_code || ''}
                    placeholder="e.g., KG, G, TON"
                    maxLength={3}
                    minLength={1}
                    pattern="[A-Za-z0-9]{1,3}"
                    title="Must be 1-3 alphanumeric characters"
                    disabled={!!editingUnit}
                    className="uppercase"
                    required
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 3);
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-1">Max 3 alphanumeric characters</p>
                </div>
                <div>
                  <Label>Commercial *</Label>
                  <Input
                    name="commercial"
                    defaultValue={editingUnit?.commercial || ''}
                    placeholder="e.g., KG"
                    maxLength={10}
                    className="uppercase"
                    required
                  />
                </div>
              </div>

              <div>
                <Label>Measurement Unit Text *</Label>
                <Input
                  name="measurement_unit_text"
                  defaultValue={editingUnit?.measurement_unit_text || ''}
                  placeholder="e.g., Kilogram"
                  required
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingUnit ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default WeightUnit;
